/* eslint-disable max-lines */
import { notEmpty, scmpPlus, theme } from "@product/scmp-sdk";
import enc from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import { addMonths, endOfDay, startOfDay } from "date-fns";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { data as appData, article as articleData } from "shared/data";
import { section as sectionData } from "shared/data/section";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { TakeoverAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot";
import { AppFooter } from "scmp-app/components/app-footer";
import { ClientSideSuspense } from "scmp-app/components/common/client-side-suspense";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { HomeAdvertisers } from "scmp-app/components/home/advertisers";
import { AiArticleList } from "scmp-app/components/home/ai-article-list";
import { HomeAround } from "scmp-app/components/home/around";
import { HomeChinaFutureTechStoryPackage } from "scmp-app/components/home/china-future-tech-story-package";
import { HomeClientQueryLoader } from "scmp-app/components/home/client-query-loader";
import { HomeFocusArticle } from "scmp-app/components/home/focus-article";
import { HomeHeroArticle } from "scmp-app/components/home/hero-article";
import { HomeHeroThreeArticles } from "scmp-app/components/home/hero-three-articles";
import { HomeLatest } from "scmp-app/components/home/latest";
import { MyNewsDailyFive } from "scmp-app/components/home/my-news-daily-five";
import { HomeNewsletter } from "scmp-app/components/home/newsletter";
import { HomepageClientQueryPlaceholder } from "scmp-app/components/home/placeholder";
import { HomepageAroundScmpPlaceholder } from "scmp-app/components/home/placeholder/around-scmp-placeholder";
import { HomeSecondaryStoryPackage } from "scmp-app/components/home/secondary-story-package";
import { HomeSectionTop } from "scmp-app/components/home/section-top";
import { HomeSeo } from "scmp-app/components/home/seo";
import { HomeSpotlight } from "scmp-app/components/home/spotlight";
import { HomeStoryPackage } from "scmp-app/components/home/story-package";
import { HomeSuperSplash } from "scmp-app/components/home/super-splash";
import { HomeThingsToDo } from "scmp-app/components/home/things-to-do";
import { HomeTopStories } from "scmp-app/components/home/top-stories";
import { MostPopular as MostPopularWidget } from "scmp-app/components/most-popular";
import { Multimedia as MultimediaWidget } from "scmp-app/components/multimedia";
import { PlusWidget } from "scmp-app/components/plus/plus-widget";
import { SubscriptionWidget } from "scmp-app/components/subscription-widget";
import { TrendingTopic } from "scmp-app/components/trending-topic";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import { Edition } from "scmp-app/lib/edition";
import { useGetEntityIdsByQueueItems } from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { parseQueryString } from "scmp-app/lib/utils";
import type {
  homePageQuery,
  LiveWidgetEditionEnum,
} from "scmp-app/queries/__generated__/homePageQuery.graphql";

import { DesktopMaximumAdHeight } from "./consts";
import {
  useAutoRefreshWhenIdlingForTenMinutes,
  useLayoutWorkaroundForMyNews,
  usePickMorningStudioArticles,
  useWhyRegisterHomepagePositionTriggerReference,
} from "./hooks";
import {
  AdContainer,
  AroundScmp,
  Comment,
  Container,
  ContentContainer,
  ContentWrapper,
  EighthAds,
  FifthAds,
  FifthSectionBlock,
  FirstAds,
  FirstSectionBlock,
  Focus,
  FourthAds,
  FourthSectionBlock,
  FromOurAdvertisers,
  HeroArticles,
  HeroThreeArticles,
  IpuNewsletter,
  Latest,
  MostPopular,
  Multimedia,
  MyNews,
  Newsletters,
  NinthAds,
  Plus,
  RecommendedForYou,
  SecondAds,
  SecondSectionBlock,
  SectionBlocksAndSpotlight,
  SeventhAds,
  SixthAds,
  StyledCommentWidget,
  Subscription,
  ThingsToDo,
  ThirdAds,
  ThirdSectionBlock,
  TopStories,
  YouMayHaveMissed,
} from "./styles";

const query = graphql`
  query homePageQuery(
    $applicationId: String!
    $chinaFutureTechSectionEntityId: String!
    $chinaFutureTechStoryPackageArticlesQueueName: String!
    $commentHarrySectionIds: [String]
    $commentQueueName: String!
    $dailyPulseTypeUuId: String!
    $focusQueueName: String!
    $highlightQueueName: String!
    $latestExcludeArticleTypes: [String]
    $latestExcludeSectionIds: [String]
    $liveWidgetEditionEnumValue: LiveWidgetEditionEnum!
    $morningStudioQueueName: String!
    $plusNewsAgendaEndDate: Timestamp!
    $plusNewsAgendaStartDate: Timestamp!
    $plusNewsAgendaTypes: [String]!
    $scmpPlusPaywallTypeIds: [String]
    $spotlightQueueName: String!
    $storyPackageArticlesQueueName: String!
    $secondaryStoryPackageArticlesQueueName: String!
    $topStoriesQueueName: String!
  ) {
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...heroThreeArticlesQueueItemsEdge
          ...topStoriesQueueItemsEdge
          node {
            ...heroArticleArticle
          }
        }
      }
    }
    morningStudioQueue: queue(filter: { name: $morningStudioQueueName }) {
      ...hooksPickMorningStudioArticle
    }
    ...newsletterHomeNewsletterQuery
    ...latestContentsQuery
      @arguments(
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        latestExcludeSectionIds: $latestExcludeSectionIds
        latestExcludeArticleTypes: $latestExcludeArticleTypes
      )
    ...thingsToDoQueue
    ...commentQueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...chinaFutureTechStoryPackageQuery
      @arguments(
        sectionEntityId: $chinaFutureTechSectionEntityId
        queueName: $chinaFutureTechStoryPackageArticlesQueueName
      )
    ...secondaryStoryPackageQuery
      @arguments(secondaryStoryPackageArticlesQueueName: $secondaryStoryPackageArticlesQueueName)
    ...storyPackageQuery @arguments(storyPackageArticlesQueueName: $storyPackageArticlesQueueName)
    ...spotlightQuery @arguments(spotlightQueueName: $spotlightQueueName)
    ...focusArticleHomeFocusArticleQuery @arguments(focusQueueName: $focusQueueName)
    ...superSplashQuery @arguments(edition: $liveWidgetEditionEnumValue)
    ...plusWidgetQuery
      @arguments(
        applicationId: $applicationId
        dailyPulseTypeUuId: $dailyPulseTypeUuId
        highlightQueueName: $highlightQueueName
        plusNewsAgendaEndDate: $plusNewsAgendaEndDate
        plusNewsAgendaStartDate: $plusNewsAgendaStartDate
        plusNewsAgendaTypes: $plusNewsAgendaTypes
      )

    commentQueue: queue(filter: { name: $commentQueueName }) {
      items(first: 5) {
        edges {
          ...contentUtilsGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...contentUtilsGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    storyPackageArticles: queue(filter: { name: $storyPackageArticlesQueueName }) {
      items(first: 3) {
        edges {
          ...contentUtilsGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    spotlightQueue: queue(filter: { name: $spotlightQueueName }) {
      items(first: 4) {
        edges {
          ...contentUtilsGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    secondaryStoryPackageArticles: queue(
      filter: { name: $secondaryStoryPackageArticlesQueueName }
    ) {
      items(first: 4) {
        edges {
          ...contentUtilsGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
  }
`;

const HomePage: RelayComponent<PageProps, homePageQuery> = ({ preloadedQuery, randomSeed }) => {
  const { query: routeQuery } = useRouter();
  const edition = routeQuery.edition as Edition;
  const isAsiaEdition = edition === Edition.Asia;
  const data = usePreloadedQuery(query, preloadedQuery);

  const adZone = useMemo(() => `homepage_${edition}/home`, [edition]);

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
      },
      zone: adZone,
    },
  });

  useAutoRefreshWhenIdlingForTenMinutes();

  const [
    heroArticle,
    heroThreeArticle1,
    heroThreeArticle2,
    heroThreeArticle3,
    topStoryArticle1,
    topStoryArticle2,
    topStoryArticle3,
    ...rest
  ] = data?.topStories?.items?.edges?.filter(item => notEmpty(item)) ?? [];

  const [morningStudioArticleSlot1, morningStudioArticleSlot2] = usePickMorningStudioArticles(
    randomSeed ?? "fallback seed",
    data.morningStudioQueue,
  );

  const heroThreeArticles = [heroThreeArticle1, heroThreeArticle2, heroThreeArticle3];

  const topStories = [
    topStoryArticle1,
    topStoryArticle2,
    topStoryArticle3,
    morningStudioArticleSlot1,
    ...rest,
  ];

  const { isSecondSectionHigherThanMyNews, setMyNewsElement, setSecondSectionBlockElement } =
    useLayoutWorkaroundForMyNews(edition);

  const whyRegisterHomepagePositionTriggerReference =
    useWhyRegisterHomepagePositionTriggerReference();

  const ssrContentEntityIds = useGetEntityIdsByQueueItems([
    ...(data.spotlightQueue?.items?.edges ?? []),
    ...(data.commentQueue?.items?.edges ?? []),
    ...(data.topStories?.items?.edges ?? []),
    ...(data.storyPackageArticles?.items?.edges ?? []),
    ...(data.secondaryStoryPackageArticles?.items?.edges ?? []),
  ]);

  return (
    <>
      <HomeSeo edition={edition} />
      <Container>
        <ContentContainer>
          <HomeSuperSplash reference={data} />
          <ContentWrapper
            $edition={edition}
            $isSecondSectionHigherThanMyNews={isSecondSectionHigherThanMyNews}
          >
            <IpuNewsletter>
              <AdSlot
                adUnit="d_banner3"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[300, 50]]}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                zone={adZone}
              />
            </IpuNewsletter>
            <HeroArticles>
              {heroArticle && <HomeHeroArticle reference={heroArticle.node} />}
            </HeroArticles>
            <FirstAds>
              <AdContainer>
                <AdSlot
                  adUnit="d_lrec1"
                  breakpoint={theme.breakpoints.up("tablet")}
                  sizes={[[300, 250], "fluid"]}
                  targeting={{
                    paid: TargetingPaidType.Free,
                  }}
                  withLabel
                  zone={adZone}
                />
                <AdSlot
                  adUnit="m_banner1"
                  breakpoint={theme.breakpoints.only("mobile")}
                  sizes={[[300, 50], [320, 50], [300, 100], [320, 100], [300, 250], "fluid"]}
                  targeting={{
                    paid: TargetingPaidType.Free,
                  }}
                  withLabel
                  zone={adZone}
                />
              </AdContainer>
            </FirstAds>
            <HeroThreeArticles>
              {heroThreeArticles.length > 0 && (
                <HomeHeroThreeArticles reference={heroThreeArticles} />
              )}
            </HeroThreeArticles>
            <Focus>
              <HomeFocusArticle pageType="homepage" reference={data} />
            </Focus>
            <TopStories>
              <HomeTopStories adZone={adZone} reference={topStories} />
            </TopStories>
            <Plus>
              <PlusWidget pageType="homepage" reference={data} sampleContent="homepage" />
            </Plus>
            <Comment>
              <StyledCommentWidget reference={data} />
            </Comment>
            <Latest>
              <HomeLatest reference={data} />
            </Latest>
            <ThingsToDo $edition={edition}>
              {edition === Edition.HongKong && <HomeThingsToDo reference={data} />}
            </ThingsToDo>
            <SecondAds>
              <AdSlot
                adUnit="d_lrec3"
                breakpoint={theme.breakpoints.up("tablet")}
                sizes={[[300, 250], [300, DesktopMaximumAdHeight], "fluid"]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />
            </SecondAds>
            <ThirdAds>
              <AdSlot
                adUnit="d_lrec3a"
                breakpoint={theme.breakpoints.up("tablet")}
                sizes={[[300, 250], "fluid"]}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                withBackground
                withLabel
                zone={adZone}
              />
            </ThirdAds>
            <Newsletters>
              <HomeNewsletter reference={data} />
            </Newsletters>
            <SectionBlocksAndSpotlight>
              <HomeStoryPackage reference={data} />
              <HomeSecondaryStoryPackage reference={data} />
              <HomeChinaFutureTechStoryPackage reference={data} />
              <div ref={whyRegisterHomepagePositionTriggerReference}>
                <HomeSpotlight reference={data} />
              </div>
            </SectionBlocksAndSpotlight>
            <FourthAds>
              <AdSlot
                adUnit="d_lrec2"
                breakpoint={theme.breakpoints.up("tablet")}
                sizes={[[300, 250], [300, DesktopMaximumAdHeight], "fluid"]}
                stickySpacing={60}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                withBackground
                withLabel
                withSticky
                zone={adZone}
              />

              <AdContainer>
                <AdSlot
                  adUnit="m_lrec3"
                  breakpoint={theme.breakpoints.only("mobile")}
                  sizes={[[300, 250], [320, 480], "fluid"]}
                  targeting={{
                    paid: TargetingPaidType.Free,
                  }}
                  withLabel
                  zone={adZone}
                />
              </AdContainer>
            </FourthAds>

            <ErrorBoundary fallback={<AppFooter />}>
              <ClientSideSuspense fallback={<HomepageClientQueryPlaceholder />}>
                <HomeClientQueryLoader ssrContentEntityIds={ssrContentEntityIds}>
                  {({ aiWidgets, data, existingEntityIds }) => (
                    <>
                      <Multimedia>
                        <MultimediaWidget reference={data} />
                      </Multimedia>
                      <RecommendedForYou>
                        {aiWidgets[AiEngineDataType.HomeRecommendedForYou].entityIds.length > 0 && (
                          <AiArticleList
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeRecommendedForYou]}
                            sponsoredReference={morningStudioArticleSlot2.node}
                            variant={AiEngineDataType.HomeRecommendedForYou}
                          />
                        )}
                      </RecommendedForYou>
                      <FirstSectionBlock>
                        {isAsiaEdition ? (
                          <HomeSectionTop reference={data} section="twia" />
                        ) : (
                          <HomeSectionTop reference={data} section="hong-kong" />
                        )}
                      </FirstSectionBlock>
                      <Subscription>
                        <SubscriptionWidget />
                      </Subscription>
                      <SecondSectionBlock>
                        {isAsiaEdition ? (
                          <HomeSectionTop
                            ref={element => setSecondSectionBlockElement?.(element)}
                            reference={data}
                            section="people-culture"
                          />
                        ) : (
                          <HomeSectionTop
                            ref={element => setSecondSectionBlockElement?.(element)}
                            reference={data}
                            section="lifestyle"
                          />
                        )}
                      </SecondSectionBlock>
                      <YouMayHaveMissed>
                        {aiWidgets[AiEngineDataType.HomeYouMayHaveMissed].entityIds.length > 0 && (
                          <AiArticleList
                            aiEngineResponse={aiWidgets[AiEngineDataType.HomeYouMayHaveMissed]}
                            variant={AiEngineDataType.HomeYouMayHaveMissed}
                          />
                        )}
                      </YouMayHaveMissed>
                      <MyNews $edition={edition}>
                        <MyNewsDailyFive ref={element => setMyNewsElement?.(element)} />
                      </MyNews>
                      <ThirdSectionBlock>
                        <HomeSectionTop reference={data} section="china" />
                      </ThirdSectionBlock>
                      <FourthSectionBlock>
                        {isAsiaEdition ? (
                          <HomeSectionTop reference={data} section="lifestyle" />
                        ) : (
                          <HomeSectionTop reference={data} section="people-culture" />
                        )}
                      </FourthSectionBlock>
                      <FifthSectionBlock>
                        <HomeSectionTop reference={data} section="style" />
                      </FifthSectionBlock>
                      <MostPopular>
                        <MostPopularWidget
                          reference={data}
                          trendingTopicSlot={<TrendingTopic reference={data} />}
                        />
                      </MostPopular>
                      <FromOurAdvertisers>
                        {data?.advertisers?.items && (
                          <HomeAdvertisers reference={data?.advertisers?.items} />
                        )}
                      </FromOurAdvertisers>
                      {/* For preventing AroundScmp is too slow and blocking the rendering  */}
                      <ClientSideSuspense
                        fallback={
                          <>
                            <AroundScmp>
                              <HomepageAroundScmpPlaceholder />
                            </AroundScmp>
                          </>
                        }
                      >
                        <AroundScmp>
                          <HomeAround existingEntityIds={existingEntityIds} />
                        </AroundScmp>
                        <AppFooter />
                      </ClientSideSuspense>
                    </>
                  )}
                </HomeClientQueryLoader>
                <FifthAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4"
                      breakpoint={theme.breakpoints.up("tablet")}
                      sizes={[[300, 250], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />
                    <AdSlot
                      adUnit="m_lrec3c"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[[300, 250], [320, 480], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </FifthAds>
                <SixthAds>
                  {edition !== Edition.HongKong && (
                    <AdSlot
                      adUnit="d_lrec4a"
                      breakpoint={theme.breakpoints.up("tablet")}
                      sizes={[[300, 250], [300, DesktopMaximumAdHeight], "fluid"]}
                      stickySpacing={60}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withBackground
                      withLabel
                      withSticky
                      zone={adZone}
                    />
                  )}
                  <AdContainer>
                    {edition === Edition.HongKong && (
                      <AdSlot
                        adUnit="d_lrec4a"
                        breakpoint={theme.breakpoints.up("tablet")}
                        sizes={[[300, 250], "fluid"]}
                        targeting={{
                          paid: TargetingPaidType.Free,
                        }}
                        withLabel
                        zone={adZone}
                      />
                    )}

                    <AdSlot
                      adUnit="m_lrec2"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[[300, 250], [320, 480], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </SixthAds>
                <SeventhAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4d"
                      breakpoint={theme.breakpoints.up("tablet")}
                      sizes={[[300, 250], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />

                    <AdSlot
                      adUnit="m_lrec3g"
                      breakpoint={theme.breakpoints.only("mobile")}
                      sizes={[[300, 250], [320, 480], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </SeventhAds>
                <EighthAds>
                  <AdContainer>
                    <AdSlot
                      adUnit="d_lrec4b"
                      breakpoint={theme.breakpoints.up("tablet")}
                      sizes={[[300, 250], "fluid"]}
                      targeting={{
                        paid: TargetingPaidType.Free,
                      }}
                      withLabel
                      zone={adZone}
                    />
                  </AdContainer>
                </EighthAds>
                <NinthAds>
                  <AdSlot
                    adUnit="m_lrec3h"
                    breakpoint={theme.breakpoints.only("mobile")}
                    sizes={[[300, 250], [320, 480], "fluid"]}
                    targeting={{
                      paid: TargetingPaidType.Free,
                    }}
                    withLabel
                    zone={adZone}
                  />
                </NinthAds>
                <TakeoverAdSlot adSlotProps={{ zone: adZone }} variant="home" />
              </ClientSideSuspense>
            </ErrorBoundary>
          </ContentWrapper>
        </ContentContainer>
      </Container>
    </>
  );
};

HomePage.displayName = "HomePage";

const pageProps: PageProps = {
  appBarConfiguration: {
    desktopBreakpoint: "desktop",
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/home",
  },
  appInitializerConfiguration: {
    hasTrendingNavBar: true,
  },
};

const generateRandomSeed = () => SHA256(new Date().toString()).toString(enc);

export default withRelay(HomePage, query, {
  clientSideProps: () => ({ ...pageProps, randomSeed: generateRandomSeed() }),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve({ ...pageProps, randomSeed: generateRandomSeed() }),
  variablesFromContext(context) {
    const edition = parseQueryString(context.query.edition) ?? "int";
    const liveWidgetEditionEnumValue: LiveWidgetEditionEnum =
      edition.toLocaleUpperCase() as Uppercase<Edition>;
    const startDate = startOfDay(new Date()).getTime();
    const endDate = addMonths(endOfDay(new Date()), 1).getTime();

    return {
      applicationId: appData.application.scmp.entityId,
      chinaFutureTechSectionEntityId: sectionData.chinaFutureTech.entityId,
      chinaFutureTechStoryPackageArticlesQueueName: `section_top_${sectionData.chinaFutureTech.entityId}`,
      commentHarrySectionIds: [sectionData.comment.harrysView.entityUuid],
      commentQueueName: `opinion_top_${edition}`,
      dailyPulseTypeUuId: articleData.types.dailyPulseBriefing.entityUuid,
      edition,
      focusQueueName: `homepage_focus_${edition}`,
      highlightQueueName: "scmp_plus_home_news",
      latestExcludeArticleTypes: [
        articleData.types.series.entityUuid,
        articleData.types.letters.entityUuid,
        articleData.types.recap.entityUuid,
      ],
      latestExcludeSectionIds: [
        sectionData.posties.entityUuid,
        sectionData.yp.entityUuid,
        sectionData.recap.entityUuid,
        sectionData.cooking.entityUuid,
      ],
      liveWidgetEditionEnumValue,
      morningStudioQueueName: `homepage_morning_studio_${edition}`,
      plusNewsAgendaEndDate: endDate,
      plusNewsAgendaStartDate: startDate,
      plusNewsAgendaTypes: [scmpPlus.topicType.newsAgenda],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
      secondaryStoryPackageArticlesQueueName: `homepage_package_top_2_${edition}`,
      spotlightQueueName: `scmp_signatures_top_${edition}`,
      storyPackageArticlesQueueName: `homepage_package_top_${edition}`,
      topStoriesQueueName: `homepage_top_${edition}`,
    };
  },
});
